import { useState, KeyboardEvent } from 'react';
import GlobalAlert from '@/components/GlobalAlert';
import {
  formattedCount,
  SettlementCountByType
} from '@/apis/settlement/interface';
import { CostReimbursementReasonMap } from '@/services/settlements/interface';

export interface KeyActionPairProps {
  matchKey: string;
  action: () => void;
}

export const onPressKeyboard = (
  keyActionPair: Array<KeyActionPairProps>,
  pressKey: string
) => {
  const keyAction = (callback: () => void) => {
    return callback();
  };

  const matched = keyActionPair.some(item => {
    if (item.matchKey === pressKey) {
      keyAction(item.action);
    }
  });

  return matched ? keyAction : false;
};

export const useKeyCapture: () => [string, any] = () => {
  const [key, setKey] = useState<string>('');

  const captureKey: any = (e: any) => {
    setKey(e.key);
    return;
  };

  return [key as string, captureKey as any];
};

export const catchDash = (key: string) => (e: any) => {
  const isDash = e.target.value.includes('-') && key !== 'Backspace';
  if (isDash) {
    GlobalAlert.show({
      title: '- 는 입력할수 없습니다.'
    });
  }
};

export const getRemainingTime = (time: number) => {
  return new Date(time).toISOString().substr(14, 5);
};

export const checkInput = (watch: Record<string, any>) => {
  return Object.values(watch).every(input => input);
};

export const formatPhoneDefault = (phone?: string) => {
  if (!phone) return '';

  const splitPoint = phone?.length === 11 ? 7 : 6;
  const first = phone?.substring(0, 3);
  const middle = phone?.substring(3, splitPoint);
  const last = phone?.substring(splitPoint);
  return `${first}-${middle}-${last}`;
};

export const trimAndFormatAnyContact = (contact?: string) => {
  if (!contact) return '';
  const trimmedNumber = `${contact}`.replace(/\D/g, '');
  const formatter = getFormatter(contact);
  return formatter(trimmedNumber);
};

const getFormatter = (number: string) => {
  // 여기 너무 냄새남.
  if (number.match(/^050/)) return formatSafetyNumber;
  if (number.match(/^[^0]/)) return formatBusinessTel;
  return formatPhoneAndTel;
};

const formatSafetyNumber = (safetyNumber: string) => {
  const pattern = /^(050\d)(\d{3,4})(\d{4})$/;
  return `${safetyNumber}`.replace(pattern, `$1-$2-$3`);
};

const formatPhoneAndTel = (phoneAndTel: string) => {
  const pattern = /^(02|0\d{2})(\d{3,4})(\d{4})$/;
  return `${phoneAndTel}`.replace(pattern, `$1-$2-$3`);
};

const formatBusinessTel = (businessTel: string) => {
  const pattern = /^(\d{4})(\d{4})$/;
  return `${businessTel}`.replace(pattern, `$1-$2`);
};

export const formatTelDefault = (tel?: string) => {
  let _tel = tel ?? '';
  /**
   * https://doctornowdev.atlassian.net/browse/DEV-726
   */
  _tel = _tel.replace(/-/g, '');
  const full = 11;

  const firstSplit = _tel?.substring(0, 2) === '02' ? 2 : 3;
  const otherSplit =
    _tel?.length === full ? 7 : _tel?.length === full - 1 ? 6 : 5;

  const first = _tel?.substring(0, firstSplit);
  const middle = _tel?.substring(firstSplit, otherSplit);
  const last = _tel?.substring(otherSplit);

  return `${first}-${middle}-${last}`;
};

export const formatComma = (x: string | number) => {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') ?? '0';
};

export const preventArrowKeys = (e: KeyboardEvent) => {
  if ([' ', 'ArrowUp', 'ArrowDown'].indexOf(e.key) > -1) {
    e.preventDefault();
  }
};

export const submitEnterKey = (e: KeyboardEvent, submit: () => void) => {
  e.key === 'Enter' && submit();
};

export const settlementMethodFormat = (
  settlement: Array<SettlementCountByType>
) => {
  const method = {
    delivery: 0,
    quick: 0,
    post: 0,
    overnight: 0,
    dawn: 0
  };
  const newMethod: formattedCount = { ...method };
  settlement.map((i: SettlementCountByType) => (newMethod[i.type] = i.count));
  newMethod.totalCount = Object.values(newMethod).reduce((a, b) => a + b, 0);

  return newMethod;
};

export const parseVarianceReason = (reason?: CostReimbursementReasonMap) => {
  if (!reason) return '-';
  return CostReimbursementReasonMap[reason] ?? '-';
};
